import React, { useEffect, useRef, useState } from 'react';
import { PlusCircleIcon, XCircleIcon } from 'lucide-react';

import { Badge } from '@Components';
import { Options } from '@Models';

type MultiSelectProps = {
  placeholder: string;
  emptyText: string;
  options: Options[];
  selected: Options[];
  onChange: (selected: Options[]) => void;
};

const MultiSelect: React.FC<MultiSelectProps> = ({ placeholder, emptyText, options, selected, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleSelect = (option: Options) => {
    if (selected.some((sel) => sel.value === option.value)) {
      onChange(selected.filter((sel) => sel.value !== option.value));
    } else {
      onChange([...selected, option]);
    }
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      <div
        className="border-solid grow border-[1px] border-gray-200 rounded-lg px-3 min-h-8 py-2 flex justify-between items-center gap-8 cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}>
        <div className=" flex flex-wrap gap-1 items-center cursor-pointer">
          {selected.length === 0 ? (
            <span className="text-gray-600">{placeholder}</span>
          ) : (
            selected.length > 0 &&
            selected.map((option) => (
              <span
                key={option.value}
                className="cursor-pointer"
                onClick={(e) => {
                  toggleSelect(option);
                  e.stopPropagation();
                }}>
                <Badge variant="orange" label={option.label} icon={<XCircleIcon className="size-4" />} />
              </span>
            ))
          )}
        </div>
        <span
          onClick={(e) => {
            onChange([]);
            e.stopPropagation();
          }}
          className="cursor-pointer grid place-content-center">
          <XCircleIcon className="size-4 text-gray-700" />
        </span>
      </div>

      {isOpen && (
        <div className="absolute mt-1 w-[93.5%] bg-white border-solid border-[1px] shadow-md border-gray-200 rounded-lg overflow-auto flex flex-wrap gap-2 p-4">
          {options.length === 0 && <span className="text-gray-600">{emptyText}</span>}
          {options.map((option) => (
            <span
              key={option.value}
              className="cursor-pointer"
              onClick={(e) => {
                toggleSelect(option);
                e.stopPropagation();
              }}>
              <Badge variant="gray" label={option.label} icon={<PlusCircleIcon className="size-4" />} />
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
